import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Badge } from "../components/Badge";
import { VerifiedPartnerBadge } from "../components/VerifiedBadge";
import { formatINR, resolveImg } from "../lib/format";
import { Claims, Quotations, BillingEntities, RFQs, DailyRates, QuoteRequests, RewardsLeaderboard, KycVerifications, AgreementsManager } from "../components/AdminSections";
import { AdminBuyerQuotations } from "../components/AdminBuyerQuotations";
import { AdminCommunications } from "../components/AdminCommunications";
import { AdminNotifications } from "../components/AdminNotifications";
import { AdminCertificates } from "../components/AdminCertificates";
import { MeetingsPanel } from "../components/Meetings";
import { VerificationCallButton, GoogleMeetConnectionCard } from "../components/VerificationCall";
import { AdminInvoiceHub } from "../components/AdminInvoiceHub";
import { AdminLetterheadStudio } from "../components/AdminLetterheadStudio";
import { AdminReinstatementInbox } from "../components/AdminReinstatementInbox";
import { AdminVirtualMeetings } from "../components/AdminVirtualMeetings";
import { AdminRfqBroadcast, AdminCreditLimits, AdminSettlementDesk } from "../components/AdminTradeSections";
import { AdminBatchLinkDependents } from "../components/AdminBatchLinkDependents";
import { AdminOnboardingInbox } from "../components/AdminOnboardingInbox";
import { AdminHierarchyTree } from "../components/AdminHierarchyTree";
import { OperationsMap } from "../components/OperationsMap";
import { DecisionModal, DecisionLogDrawer } from "../components/AdminDecisionModal";
import { AdminMasterLedger } from "../components/AdminMasterLedger";
import { AdminPriceLockBoard } from "../components/AdminPriceLockBoard";
import { AdminWalletPanel } from "../components/AdminWalletPanel";
import { AdminMonumentalStudio } from "../components/AdminMonumentalStudio";
import { AdminComplianceCapsule } from "../components/AdminComplianceCapsule";
import { AdminBankerReport } from "../components/AdminBankerReport";
import { AdminSovereignLedger } from "../components/AdminSovereignLedger";
import { AdminSignatoryVault } from "../components/AdminSignatoryVault";
import { AdminBrandAssets } from "../components/AdminBrandAssets";
import { AdminCommissionDesk } from "../components/AdminCommissionDesk";
import { AdminDealProtection } from "../components/AdminDealProtection";
import { AdminSellerRanking } from "../components/AdminSellerRanking";
import { AdminBigCapLeague } from "../components/AdminBigCapLeague";
import { AdminDirectorSuite } from "../components/AdminDirectorSuite";
import { KYCReviewPanel } from "../components/director/KYCReviewPanel";
import { AdminBoardMotions } from "../components/AdminBoardMotions";
import { AdminFounderConcierge } from "../components/AdminFounderConcierge";
import { AdminDataGovernance } from "../components/AdminDataGovernance";
import { CustodianKitCard } from "../components/CustodianKitCard";
import { AdminDirectorInvites } from "../components/DirectorInvitesAndPhoto";
import { PioneersPanel, PioneerAccessControl } from "../components/admin/pioneers";
import { AdminPaymentRails } from "../components/admin/AdminPaymentRails";
import { AdminChairmanSignature } from "../components/AdminChairmanSignature";
import { AdminRefundCompliance } from "../components/AdminRefundCompliance";
import { AdminComplianceLog } from "../components/AdminComplianceLog";
import { AdminFeeManagement } from "../components/AdminFeeManagement";
import { AdminIndirectClaims } from "../components/AdminIndirectClaims";
import { AdminSRNLedger } from "../components/AdminSRNLedger";
import { AdminLegacyGoodwill } from "../components/AdminLegacyGoodwill";
import AdminFinancialRules from "../components/AdminFinancialRules";
import AdminPricePulseCard from "../components/AdminPricePulseCard";
import { AdminSRNPalette, useSRNPalette } from "../components/AdminSRNPalette";
import { AdminPulseAnalyticsTile } from "../components/AdminPulseAnalyticsTile";
import { AdminIMAPHealthTile } from "../components/AdminIMAPHealthTile";
import { AdminPayoutsTile } from "../components/AdminPayoutsTile";
import { AdminZohoBooksPanel } from "../components/AdminZohoBooksPanel";
import { AdminCareersPanel } from "../components/AdminCareersPanel";
import { AdminLegalDeskPanel } from "../components/AdminLegalDeskPanel";
import { AdminLivePulseTile, AdminLivePulsePanel } from "../components/AdminLivePulsePanel";
import { AdminTreasuryDesk } from "../components/AdminTreasuryDesk";
import { AdminTreasuryOnboarding } from "../components/AdminTreasuryOnboarding";
import { AdminSealDesk } from "../components/AdminSealDesk";
import { AdminGstDesk } from "../components/AdminGstDesk";
import { AdminBillDesk } from "../components/AdminBillDesk";
import { AdminVCB } from "../components/AdminVCB";
import { AdminPropertyDesk } from "../components/AdminPropertyDesk";
import { AdminManual } from "../components/AdminManual";
import { AdminShell } from "../components/admin/AdminShell";
import { AdminPayoutsDesk } from "../components/AdminPayoutsDesk";
import { KpiDashboard } from "../components/admin/KpiDashboard";
import { QuickActions } from "../components/admin/QuickActions";

function Inquiries({ api }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { setItems((await api.get("/inquiries")).data); }
    catch { toast.error("Could not load inquiries."); }
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <p className="font-body text-[#8A8A82]">Loading…</p>;
  if (items.length === 0) return <p className="font-body text-[#8A8A82]">No inquiries yet.</p>;
  return (
    <div className="space-y-4" data-testid="admin-inquiry-list">
      {items.map((it) => (
        <div key={it.id} data-testid="admin-inquiry-row" className="border border-white/10 rounded-sm p-6 md:p-8 hover:border-white/20 transition-colors">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="font-serif text-2xl">{it.first_name} {it.last_name}</div>
              <div className="font-body text-sm text-[#8A8A82] mt-1">{it.email}{it.phone ? ` · ${it.phone}` : ""}{it.company ? ` · ${it.company}` : ""}</div>
            </div>
            <div className="text-right">
              {it.interest && <span className="inline-block font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#B87333] border border-[#B87333]/40 rounded-full px-3 py-1">{it.interest}</span>}
              <div className="font-body text-xs text-[#8A8A82] mt-2">{new Date(it.created_at).toLocaleString()}</div>
            </div>
          </div>
          <p className="font-body text-[#C9C9C2] leading-relaxed mt-4 border-t border-white/10 pt-4">{it.message}</p>
        </div>
      ))}
    </div>
  );
}

function UserApprovals({ api, onCount }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [tab, setTab] = useState("awaiting_approval");
  const [roleFilter, setRoleFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [decisionTarget, setDecisionTarget] = useState(null);
  const [logTarget, setLogTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams({ status: tab });
      if (roleFilter) q.set("role", roleFilter);
      if (categoryFilter) q.set("buyer_category", categoryFilter);
      setItems((await api.get(`/admin/users?${q.toString()}`)).data);
      onCount?.();
    }
    catch { toast.error("Could not load users."); }
    finally { setLoading(false); }
  }, [api, tab, roleFilter, categoryFilter, onCount]);
  useEffect(() => { load(); }, [load]);

  const approve = async (u) => {
    if (!window.confirm(`Approve ${u.business_name || u.email}?\nThis will grant full platform access and email a Welcome to Vinci Inc. World One Trade note.`)) return;
    setApproving(u.id);
    try {
      await api.post(`/admin/users/${u.id}/approve`);
      toast.success(`${u.business_name || u.email} approved — welcome email sent.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Approval failed."); }
    finally { setApproving(null); }
  };

  const bulkApprove = async () => {
    if (items.length === 0) return;
    if (!window.confirm(`Approve ALL ${items.length} partner${items.length === 1 ? "" : "s"} waiting for review?\nEach will receive a Welcome to Vinci Inc. World One Trade email.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/users/bulk-approve", {});
      toast.success(`${data.approved} approved · ${data.email_sent} emails sent${data.email_failed ? ` · ${data.email_failed} email failures` : ""}.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk approval failed."); }
    finally { setBulkBusy(false); }
  };

  const reject = async (u) => {
    const reason = window.prompt(`Reject ${u.business_name || u.email}?\n\nAdd a short note explaining why (they'll receive this by email). Leave blank to revoke silently.`);
    if (reason === null) return; // cancel pressed
    const trimmed = reason.trim();
    const body = trimmed ? { reason: trimmed, notify: true } : {};
    try {
      const { data } = await api.post(`/admin/users/${u.id}/revoke`, body);
      toast.success(trimmed ? `Rejected — email sent to ${u.email}.` : "Access revoked (no email sent).");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed."); }
  };

  const stagePill = (s) => {
    const map = {
      pending_kyc: { c: "#B87333", l: "eKYC pending" },
      awaiting_approval: { c: "#D8B23E", l: "Awaiting approval" },
      active: { c: "#2FB999", l: "Active" },
      rejected: { c: "#C0392B", l: "Rejected" },
      cancelled: { c: "#7a1e15", l: "Revoked" },
    };
    const m = map[s] || map.pending_kyc;
    return <span className="font-grotesk text-[10px] uppercase tracking-[0.18em] border rounded-full px-3 py-1" style={{ color: m.c, borderColor: `${m.c}66` }}>{m.l}</span>;
  };

  return (
    <div data-testid="admin-user-approvals">
      <div className="rounded-sm border border-[#B87333]/40 bg-[#B87333]/[0.06] p-5 mb-6 flex items-start gap-4">
        <span className="grid place-items-center w-10 h-10 rounded-full bg-[#B87333] text-black shrink-0">✓</span>
        <div>
          <div className="overline text-[#B87333]">Manual approval gate</div>
          <p className="font-body text-sm text-[#C9C9C2] mt-1 leading-relaxed">
            Every new signup completes eKYC first, then waits here for your manual review. Approving a user
            unlocks the Shop, Cart and Partner Network and triggers an automated <strong>Welcome to Vinci Inc. World One Trade</strong> email.
            Rejecting sends them a polite note with your reason.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-6">
        {[["awaiting_approval", "Awaiting Approval"], ["pending_kyc", "Pending eKYC"], ["active", "Active"], ["rejected", "Rejected"], ["cancelled", "Revoked"]].map(([k, l]) => (
          <button key={k} data-testid={`approvals-filter-${k}`} onClick={() => setTab(k)}
            className={`rounded-full px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.18em] transition-colors ${tab === k ? "bg-[#F5F5F0] text-black" : "border border-white/20 text-[#C9C9C2] hover:border-[#B87333]"}`}>
            {l}
          </button>
        ))}
        {tab === "awaiting_approval" && items.length > 0 && (
          <button data-testid="bulk-approve-btn" onClick={bulkApprove} disabled={bulkBusy}
            className="ml-auto rounded-full bg-[#2FB999] text-black px-5 py-2 font-grotesk text-[10px] uppercase tracking-[0.2em] hover:opacity-90 transition-opacity disabled:opacity-50">
            {bulkBusy ? "Approving all…" : `Approve all ${items.length}`}
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-6 -mt-3">
        <span className="font-grotesk text-[10px] uppercase tracking-[0.18em] text-[#8A8A82]">Narrow:</span>
        <select data-testid="approvals-role-filter" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}
                className="bg-black/40 border border-white/20 rounded-sm px-3 py-1 font-body text-xs text-[#C9C9C2]">
          <option value="">All roles</option>
          <option value="buyer">Buyers</option>
          <option value="seller">Sellers</option>
        </select>
        <select data-testid="approvals-category-filter" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
                className="bg-black/40 border border-white/20 rounded-sm px-3 py-1 font-body text-xs text-[#C9C9C2]">
          <option value="">All buyer categories</option>
          <option value="super_stockist">Super-Stockist</option>
          <option value="introducer">Introducer</option>
          <option value="guarantor">Guarantor</option>
          <option value="custodian">Custodian</option>
          <option value="wholesaler">Wholesaler</option>
          <option value="dealer">Dealer</option>
          <option value="booking_agent">Booking Agent</option>
        </select>
        {(roleFilter || categoryFilter) && (
          <button onClick={() => { setRoleFilter(""); setCategoryFilter(""); }}
                  className="font-grotesk text-[10px] uppercase tracking-[0.18em] text-[#B87333] hover:text-[#D8B23E]">
            Clear
          </button>
        )}
      </div>

      {loading ? (
        <p className="font-body text-[#8A8A82]">Loading…</p>
      ) : items.length === 0 ? (
        <p className="font-body text-[#8A8A82]" data-testid="approvals-empty">
          {tab === "awaiting_approval" ? "No partners waiting on approval right now." :
            tab === "pending_kyc" ? "Everyone has finished eKYC." :
            tab === "rejected" ? "No rejected partners." :
            tab === "cancelled" ? "No revoked accounts." : "No active partners yet."}
        </p>
      ) : (
        <div className="space-y-3">
          {items.map((u) => (
            <div key={u.id} data-testid={`approval-row-${u.id}`} className="border border-white/10 rounded-sm p-5 md:p-6 hover:border-white/20 transition-colors">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-serif text-xl md:text-2xl">{u.business_name || u.name || u.email}</span>
                    {u.vendor_code && (
                      <span
                        className="font-grotesk text-[11px] tracking-[0.2em] tabular-nums px-2.5 py-1 rounded-sm border border-[#B87333]/55 text-[#B87333] bg-[#B87333]/[0.07] font-semibold"
                        data-testid={`vendor-code-${u.id}`}
                        title="Vinci Code"
                      >
                        #{u.vendor_code}
                      </span>
                    )}
                    {stagePill(u.status)}
                    <span className="font-grotesk text-[10px] uppercase tracking-[0.18em] text-[#8A8A82]">{u.account_type}{u.buyer_category ? ` · ${u.buyer_category}` : ""}</span>
                  </div>
                  <div className="font-body text-sm text-[#8A8A82] mt-1">{u.name}{u.email ? ` · ${u.email}` : ""}{u.phone ? ` · ${u.phone}` : ""}</div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button data-testid={`decide-${u.id}`} onClick={() => setDecisionTarget(u)}
                    className="rounded-full text-black px-5 py-2 font-grotesk text-[10px] uppercase tracking-[0.2em] hover:opacity-90 transition-opacity"
                    style={{ backgroundColor: u.status === "cancelled" ? "#2FB999" : "#B87333" }}>
                    {u.status === "cancelled" ? "Restore access…" : "Decide…"}
                  </button>
                  {(u.decisions_count > 0) && (
                    <button data-testid={`decisions-log-${u.id}`} onClick={() => setLogTarget(u)}
                      className="rounded-full border border-white/20 px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.2em] hover:border-white/40 transition-colors">
                      History ({u.decisions_count})
                    </button>
                  )}
                  {u.status === "awaiting_approval" && (
                    <button data-testid={`approve-${u.id}`} onClick={() => approve(u)} disabled={approving === u.id}
                      className="rounded-full bg-[#2FB999] text-black px-5 py-2 font-grotesk text-[10px] uppercase tracking-[0.2em] hover:opacity-90 transition-opacity disabled:opacity-50">
                      {approving === u.id ? "Approving…" : "Quick approve"}
                    </button>
                  )}
                </div>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-3 mt-5 pt-5 border-t border-white/10">
                {u.gst_number && <div><div className="overline text-[#8A8A82]">GSTIN</div><div className="font-body text-sm text-[#C9C9C2] mt-1">{u.gst_number}</div></div>}
                {u.pan && <div><div className="overline text-[#8A8A82]">PAN</div><div className="font-body text-sm text-[#C9C9C2] mt-1">{u.pan}</div></div>}
                {u.state && <div><div className="overline text-[#8A8A82]">State</div><div className="font-body text-sm text-[#C9C9C2] mt-1">{u.state}</div></div>}
                {u.kyc_status && <div><div className="overline text-[#8A8A82]">KYC</div><div className="font-body text-sm mt-1" style={{ color: u.kyc_status === "verified" ? "#2FB999" : "#D8B23E" }}>{u.kyc_status}</div></div>}
                {u.address && <div className="col-span-full"><div className="overline text-[#8A8A82]">Address</div><div className="font-body text-sm text-[#C9C9C2] mt-1">{u.address}</div></div>}
                {u.created_at && <div><div className="overline text-[#8A8A82]">Signed up</div><div className="font-body text-sm text-[#C9C9C2] mt-1">{new Date(u.created_at).toLocaleDateString()}</div></div>}
                {u.approved_at && <div><div className="overline text-[#8A8A82]">Approved</div><div className="font-body text-sm text-[#2FB999] mt-1">{new Date(u.approved_at).toLocaleDateString()}</div></div>}
                {u.rejection_reason && (
                  <div className="col-span-full">
                    <div className="overline text-[#E08A7E]">Last rejection note{u.rejected_at ? ` · ${new Date(u.rejected_at).toLocaleDateString()}` : ""}</div>
                    <div className="font-body text-sm text-[#C9C9C2] mt-1 italic border-l-2 border-[#C0392B] pl-3 bg-[#C0392B]/[0.05] py-2 rounded-sm" data-testid={`rejection-note-${u.id}`}>{u.rejection_reason}</div>
                  </div>
                )}
                {u.conditions && (
                  <div className="col-span-full">
                    <div className="overline text-[#D8B23E]">
                      Conditions {u.conditions_ack_required
                        ? <span className="text-[#E08A7E]">· awaiting partner acknowledgement</span>
                        : <span className="text-[#2FB999]">· acknowledged</span>}
                    </div>
                    <div className="font-body text-sm text-[#F5F5F0] mt-1 whitespace-pre-line border-l-2 border-[#D8B23E] pl-3 bg-[#D8B23E]/[0.05] py-2 rounded-sm" data-testid={`conditions-note-${u.id}`}>{u.conditions}</div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <DecisionModal open={!!decisionTarget} user={decisionTarget}
                     onClose={() => setDecisionTarget(null)}
                     onDone={load} api={api} />
      <DecisionLogDrawer open={!!logTarget} user={logTarget}
                         onClose={() => setLogTarget(null)}
                         api={api} />
    </div>
  );
}

function Users({ api }) {
  const { user: currentUser } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kitEmails, setKitEmails] = useState([]);
  useEffect(() => {
    api.get("/admin/custodian-kits").then((r) => setKitEmails((r.data.accounts || []).map((a) => a.email.toLowerCase()))).catch(() => {});
  }, [api]);
  const load = useCallback(async () => {
    setLoading(true);
    try { setItems((await api.get("/users")).data); }
    catch { toast.error("Could not load users."); }
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const act = async (id, action) => {
    try {
      await api.post(`/users/${id}/${action}`);
      const msg = { approve: "Seller authorised.", reject: "Seller rejected.", verify: "Buyer verified." }[action];
      toast.success(msg);
      load();
    } catch { toast.error("Action failed."); }
  };

  // Alt+Click on Deactivate prompts for a reason; plain click sends a canned
  // "Access Revoked" email.  Reactivate is one-click.  Hard Delete requires a
  // simple OK/Cancel confirm dialog per board policy.
  const deactivate = async (u, ev, presetId) => {
    let reason = "";
    let preset = presetId || "";
    if (!preset && ev?.altKey) {
      reason = window.prompt(
        `Deactivate ${u.business_name || u.email}?\n\nOptional reason (appears in the Access-Revoked email and on the /access-suspended page):`,
        "",
      );
      if (reason === null) return;              // cancelled
      reason = reason.trim();
    } else if (!preset) {
      if (!window.confirm(`Deactivate ${u.business_name || u.email}?\n\nA canned Access-Revoked email will be sent.\n\nTip: hold Alt while clicking to add a personalised reason, or use a preset chip.`)) return;
    }
    try {
      await api.post(`/admin/users/${u.id}/deactivate`, preset ? { preset } : { reason });
      toast.success(`Access revoked · ${u.email}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Deactivate failed."); }
  };
  const reactivate = async (u) => {
    if (!window.confirm(`Reactivate ${u.business_name || u.email}?\n\nA restoration email will be sent.`)) return;
    try {
      await api.post(`/admin/users/${u.id}/reactivate`);
      toast.success(`Access restored · ${u.email}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reactivate failed."); }
  };
  const hardDelete = async (u) => {
    if (!window.confirm(
      `Permanently delete ${u.business_name || u.email}?\n\n` +
      `• Login + KYC + tokens will be wiped\n` +
      `• Invoices / orders / ledger entries stay on the record with a tombstone\n\n` +
      `THIS ACTION CANNOT BE UNDONE.`,
    )) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(`Deleted · ${u.email}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed."); }
  };

  if (loading) return <p className="font-body text-[#8A8A82]">Loading…</p>;
  if (items.length === 0) return <p className="font-body text-[#8A8A82]">No registered buyers or sellers yet.</p>;
  return (
    <div className="space-y-4" data-testid="admin-user-list">
      {items.map((u) => (
        <div key={u.id} data-testid="admin-user-row" className="border border-white/10 rounded-sm p-6 md:p-8 hover:border-white/20 transition-colors">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-serif text-2xl">{u.profile?.business_name || u.name}</span>
                {u.vendor_code && (
                  <span
                    className="font-grotesk text-[11px] tracking-[0.2em] tabular-nums px-2.5 py-1 rounded-sm border border-[#B87333]/55 text-[#B87333] bg-[#B87333]/[0.07] font-semibold"
                    data-testid={`vendor-code-${u.id}`}
                    title="Vinci Code"
                  >
                    #{u.vendor_code}
                  </span>
                )}
                {u.sub_role && (
                  <span
                    className="font-grotesk text-[10px] tracking-[0.18em] px-2 py-1 rounded-sm border border-white/20 text-[#C9C9C2] bg-white/[0.03]"
                    data-testid={`sub-role-${u.id}`}
                    title={u.parent_business_name ? `Parent: ${u.parent_business_name} (${u.parent_vendor_code || ''})` : ""}
                  >
                    {u.sub_role === "independent" ? "3A · Independent"
                     : u.sub_role === "introducer_dependent" ? "3B · Introducer-Dep"
                     : u.sub_role === "guarantor_dependent" ? "3C · Guarantor-Dep"
                     : u.sub_role === "custodian_dependent" ? "3D · Custodian-Dep"
                     : u.sub_role}
                  </span>
                )}
                <Badge user={u} size="sm" />
                {u.kyc_status === "verified" && <VerifiedPartnerBadge size="sm" testid={`admin-verified-partner-${u.id}`} />}
                {u.account_type === "buyer" && !u.verified && (
                  <span className="font-grotesk text-[10px] uppercase tracking-[0.18em] text-[#D8B23E] border border-[#D8B23E]/40 rounded-full px-2.5 py-1">Unverified</span>
                )}
              </div>
              <div className="font-body text-sm text-[#8A8A82] mt-1">
                {u.name}{u.email ? ` · ${u.email}` : ""}{u.profile?.phone ? ` · ${u.profile.phone}` : ""}
              </div>
            </div>
            <span className="font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#8A8A82]">{u.account_type}</span>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3 mt-5 pt-5 border-t border-white/10">
            {u.profile?.gst_number && <Field l="GST" v={u.profile.gst_number} />}
            {u.profile?.pan && <Field l="PAN" v={u.profile.pan} />}
            {u.profile?.materials_supplied && <Field l="Materials" v={u.profile.materials_supplied} />}
            {u.profile?.material_tags?.length > 0 && <Field l="Material tags" v={u.profile.material_tags.join(" · ")} />}
            {u.profile?.supply_capacity && <Field l="Capacity" v={u.profile.supply_capacity} />}
            {u.profile?.years_in_business && <Field l="Years in business" v={u.profile.years_in_business} />}
            {u.profile?.category_info && <Field l="Category details" v={u.profile.category_info} />}
            {u.profile?.address && <Field l="Address" v={u.profile.address} full />}
            {u.profile?.docs_note && <Field l="Compliance note" v={u.profile.docs_note} full />}
          </div>

          {kitEmails.includes((u.email || "").toLowerCase()) && <CustodianKitCard api={api} user={u} />}

          {u.account_type === "seller" && u.status !== "approved" && (
            <div className="flex flex-wrap items-center gap-3 mt-6">
              <button data-testid={`approve-${u.id}`} onClick={() => act(u.id, "approve")} className="rounded-full bg-[#2FB999] text-black px-5 py-2 font-grotesk text-[10px] uppercase tracking-[0.2em] hover:opacity-90 transition-opacity">Authorise seller</button>
              {u.status !== "rejected" && (
                <button data-testid={`reject-${u.id}`} onClick={() => act(u.id, "reject")} className="rounded-full border border-white/20 px-5 py-2 font-grotesk text-[10px] uppercase tracking-[0.2em] hover:border-[#C0392B] hover:text-[#C0392B] transition-colors">Reject</button>
              )}
              <VerificationCallButton api={api} currentUser={currentUser}
                target={{ id: u.id, email: u.email, business_name: u.profile?.business_name || u.name }} />
            </div>
          )}
          {u.account_type !== "seller" && (
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <VerificationCallButton api={api} currentUser={currentUser}
                target={{ id: u.id, email: u.email, business_name: u.profile?.business_name || u.name }}
                variant="ghost" />
            </div>
          )}

          {["super_stockist", "wholesaler", "dealer", "booking_agent"].includes(u.buyer_category) && (
            <LinkPartnerControl api={api} user={u} onDone={load} />
          )}

          {/* --- Deactivate / Reactivate / Hard Delete --- */}
          {u.role !== "admin" && (
            <UserActionCluster
              api={api}
              user={u}
              onDeactivate={deactivate}
              onReactivate={reactivate}
              onHardDelete={hardDelete}
            />
          )}
        </div>
      ))}
    </div>
  );
}


const DEACT_PRESETS = [
  { id: "policy_breach",    label: "Policy Breach",     accent: "#C0392B" },
  { id: "compliance_lapse", label: "Compliance Lapse",  accent: "#D8B23E" },
  { id: "payment_default",  label: "Payment Default",   accent: "#B87333" },
];


/**
 * Row-level Deactivate / Reactivate / Delete cluster with:
 *   - 3 preset chips for one-click revocation
 *   - "History" toggle that loads `/admin/users/{uid}/audit-log`
 */
function UserActionCluster({ api, user: u, onDeactivate, onReactivate, onHardDelete }) {
  const [showLog, setShowLog] = useState(false);
  const [events, setEvents] = useState(null);
  const [loadingLog, setLoadingLog] = useState(false);
  const openLog = async () => {
    setShowLog(!showLog);
    if (events || !api) return;
    setLoadingLog(true);
    try {
      const r = await api.get(`/admin/users/${u.id}/audit-log?limit=50`);
      setEvents(r.data?.events || []);
    } catch { setEvents([]); }
    finally { setLoadingLog(false); }
  };
  const fmtWhen = (iso) => { try { return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return iso; } };
  const actionLabel = {
    deactivate: "Deactivated", reactivate: "Reactivated", hard_delete: "Hard-deleted",
    reinstatement_request: "Reinstatement request", reinstatement_approved: "Reinstatement approved",
    reinstatement_rejected: "Reinstatement rejected",
  };
  const actionColor = {
    deactivate: "#C0392B", reactivate: "#2FB999", hard_delete: "#8A2C22",
    reinstatement_request: "#D8B23E", reinstatement_approved: "#2FB999", reinstatement_rejected: "#C0392B",
  };
  return (
    <div className="mt-5 pt-4 border-t border-white/[0.06]" data-testid={`user-action-cluster-${u.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#5f5f58] mr-1">Account state</span>
        {u.status === "deactivated" ? (
          <span
            data-testid={`revoked-chip-${u.id}`}
            className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#C0392B] border border-[#C0392B]/40 rounded-full px-2.5 py-1"
            title={u.deactivation_reason || ""}
          >
            Access Revoked{u.deactivation_ref ? ` · ${u.deactivation_ref}` : ""}
          </span>
        ) : (
          <>
            {DEACT_PRESETS.map((p) => (
              <button
                key={p.id}
                data-testid={`deactivate-preset-${p.id}-${u.id}`}
                onClick={() => {
                  if (window.confirm(`Deactivate ${u.business_name || u.email} for "${p.label}"?\n\nA formal Access-Revoked email with the preset reason will be sent.`)) {
                    onDeactivate(u, null, p.id);
                  }
                }}
                title={`Deactivate with preset · ${p.label}`}
                className="rounded-full border px-3 py-1.5 font-grotesk text-[10px] uppercase tracking-[0.22em] hover:text-black transition-colors"
                style={{ borderColor: p.accent + "66", color: p.accent, backgroundColor: "transparent" }}
                onMouseOver={(e) => { e.currentTarget.style.backgroundColor = p.accent; e.currentTarget.style.color = "#000"; }}
                onMouseOut={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = p.accent; }}
              >{p.label}</button>
            ))}
            <button
              data-testid={`deactivate-${u.id}`}
              onClick={(e) => onDeactivate(u, e)}
              title="Custom deactivate (Alt+Click for personalised reason)"
              className="rounded-full border border-white/20 text-[#C9C9C2] hover:border-[#C0392B] hover:text-[#C0392B] px-3 py-1.5 font-grotesk text-[10px] uppercase tracking-[0.22em]"
            >Custom…</button>
          </>
        )}
        {u.status === "deactivated" && (
          <button
            data-testid={`reactivate-${u.id}`}
            onClick={() => onReactivate(u)}
            className="rounded-full bg-[#2FB999] text-black px-4 py-1.5 font-grotesk text-[10px] uppercase tracking-[0.22em] hover:opacity-90"
          >Reactivate</button>
        )}
        <button
          data-testid={`hard-delete-${u.id}`}
          onClick={() => onHardDelete(u)}
          className="rounded-full border border-[#C0392B]/40 text-[#C0392B] hover:bg-[#C0392B] hover:text-white px-4 py-1.5 font-grotesk text-[10px] uppercase tracking-[0.22em]"
        >Hard Delete</button>
        <button
          data-testid={`audit-toggle-${u.id}`}
          onClick={openLog}
          className="ml-auto rounded-full border border-white/15 text-[#8A8A82] hover:text-[#D8B23E] hover:border-[#D8B23E] px-3 py-1.5 font-grotesk text-[10px] uppercase tracking-[0.22em]"
        >{showLog ? "Hide History" : "History"}</button>
      </div>
      {showLog && (
        <div className="mt-4 border border-white/10 rounded-sm bg-black/40" data-testid={`audit-log-${u.id}`}>
          <div className="px-4 py-2 border-b border-white/10 font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#8A8A82]">
            Lifecycle Timeline
          </div>
          <div className="max-h-64 overflow-y-auto divide-y divide-white/[0.06]">
            {loadingLog && <div className="p-4 text-xs text-[#8A8A82]">Loading…</div>}
            {!loadingLog && events && events.length === 0 && (
              <div className="p-4 text-xs text-[#8A8A82]">No lifecycle events yet.</div>
            )}
            {events?.map((ev) => (
              <div key={ev.id} className="p-3 flex items-start gap-3 text-[13px]">
                <div className="w-2 h-2 rounded-full mt-1.5 shrink-0" style={{ background: actionColor[ev.action] || "#8A8A82" }} />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-2">
                    <span className="font-serif text-[15px]" style={{ color: actionColor[ev.action] || "#F5F5F0" }}>
                      {actionLabel[ev.action] || ev.action}
                    </span>
                    <span className="font-body text-[11px] text-[#8A8A82]">by {ev.actor_email || "system"}</span>
                    <span className="font-body text-[11px] text-[#5f5f58]">· {fmtWhen(ev.at)}</span>
                  </div>
                  {ev.meta?.ref && <div className="font-mono text-[10px] text-[#D8B23E]">{ev.meta.ref}</div>}
                  {ev.meta?.reason && <div className="text-[12px] text-[#C9C9C2] italic mt-0.5">"{ev.meta.reason}"</div>}
                  {ev.meta?.preset && <div className="text-[10px] text-[#8A8A82] uppercase tracking-wide mt-0.5">preset · {ev.meta.preset}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Admin control on the Users tab — link ANY Level-3 buyer (Super-Stockist,
 * Wholesaler, Dealer, Booking Agent) to a Level-2 sponsor (Introducer,
 * Custodian or Guarantor).  Clearing the link returns the buyer to
 * Independent.  Sends a `POST /admin/users/{uid}/parent`.
 */
function LinkPartnerControl({ api, user, onDone }) {
  const [open, setOpen] = useState(false);
  const [sponsors, setSponsors] = useState(null);
  const [type, setType] = useState("");
  const [selected, setSelected] = useState(user.parent_id || "");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  const loadSponsors = useCallback(async () => {
    try {
      const r = await api.get("/hierarchy/level2");
      setSponsors(r.data || []);
    } catch {
      toast.error("Could not load Level-2 sponsors");
      setSponsors([]);
    }
  }, [api]);

  useEffect(() => { if (open && sponsors === null) loadSponsors(); }, [open, sponsors, loadSponsors]);

  const filtered = (sponsors || []).filter((s) => {
    if (type && s.category !== type) return false;
    if (q) {
      const hay = `${s.business_name || ""} ${s.vendor_code || ""} ${s.state || ""}`.toLowerCase();
      if (!hay.includes(q.trim().toLowerCase())) return false;
    }
    return true;
  });

  const save = async (parent_id) => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/users/${user.id}/parent`, { parent_id: parent_id || null });
      if (parent_id) {
        toast.success(`Linked to ${r.data.parent_business_name || "sponsor"} · ${r.data.sub_role}`);
      } else {
        toast.success("Sponsor link cleared — buyer is now Independent");
      }
      setOpen(false);
      setSelected(parent_id || "");
      onDone && onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Link failed");
    } finally { setBusy(false); }
  };

  const parentLabel = user.parent_business_name
    ? `${user.parent_business_name} · ${user.parent_category?.toUpperCase() || ""}${user.parent_vendor_code ? ` · ${user.parent_vendor_code}` : ""}`
    : "Independent (no sponsor)";

  return (
    <div data-testid={`link-partner-panel-${user.id}`} className="mt-5 pt-5 border-t border-white/[0.08]">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <span className="overline text-[#B87333]">Linked Partner</span>
          <div data-testid={`linked-partner-${user.id}`} className="font-body text-sm text-[#F5F5F0] mt-1 truncate">
            {parentLabel}
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          {user.parent_id && (
            <button
              type="button"
              data-testid={`unlink-partner-${user.id}`}
              onClick={() => save(null)}
              disabled={busy}
              className="rounded-full border border-white/20 px-4 py-1.5 font-grotesk text-[10px] uppercase tracking-[0.2em] hover:border-[#C0392B] hover:text-[#C0392B] transition-colors disabled:opacity-50"
            >
              Unlink
            </button>
          )}
          <button
            type="button"
            data-testid={`link-partner-open-${user.id}`}
            onClick={() => setOpen((v) => !v)}
            className="rounded-full bg-[#B87333] text-black px-4 py-1.5 font-grotesk text-[10px] uppercase tracking-[0.24em] hover:bg-[#A05F26] transition-colors"
          >
            {user.parent_id ? "Change" : "Link a sponsor"}
          </button>
        </div>
      </div>

      {open && (
        <div className="mt-4 border border-[#B87333]/30 rounded-sm bg-black/40 p-4">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            {["", "introducer", "custodian", "guarantor"].map((k) => (
              <button
                key={k || "all"}
                type="button"
                data-testid={`link-partner-filter-${k || "all"}-${user.id}`}
                onClick={() => setType(k)}
                className={`font-grotesk text-[9px] uppercase tracking-[0.22em] px-3 py-1 rounded-sm transition-colors ${type === k ? "bg-[#B87333] text-black" : "border border-white/15 text-[#C9C9C2] hover:border-[#B87333]"}`}
              >
                {k ? k : "All"}
              </button>
            ))}
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid={`link-partner-search-${user.id}`}
              placeholder="Search by business, code, state…"
              className="flex-1 min-w-[180px] bg-black/40 border border-white/15 rounded-sm px-3 py-1.5 font-body text-xs text-[#F5F5F0] focus:outline-none focus:border-[#B87333]"
            />
          </div>
          {sponsors === null ? (
            <p className="font-body text-xs text-[#8A8A82] py-4 text-center">Loading sponsors…</p>
          ) : filtered.length === 0 ? (
            <p className="font-body text-xs text-[#8A8A82] py-4 text-center">No active Level-2 sponsors match.</p>
          ) : (
            <ul className="max-h-72 overflow-y-auto divide-y divide-white/[0.05]">
              {filtered.map((s) => {
                const active = s.id === selected;
                return (
                  <li
                    key={s.id}
                    data-testid={`link-partner-option-${s.id}-${user.id}`}
                    className={`px-2 py-2.5 flex items-center gap-3 cursor-pointer rounded-sm ${active ? "bg-[#B87333]/[0.08]" : "hover:bg-white/[0.03]"}`}
                    onClick={() => !busy && save(s.id)}
                  >
                    <span className="font-grotesk text-[9px] uppercase tracking-[0.22em] px-2 py-0.5 rounded-sm border border-[#B87333]/40 text-[#B87333] bg-[#B87333]/[0.05]">
                      {s.category}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="font-serif text-sm text-[#F5F5F0] truncate">{s.business_name}</div>
                      <div className="font-grotesk text-[9px] uppercase tracking-[0.2em] text-[#8A8A82] truncate">
                        {s.vendor_code}{s.state ? ` · ${s.state}` : ""}
                      </div>
                    </div>
                    {active ? (
                      <span className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#B87333]">Current</span>
                    ) : (
                      <span className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82] hover:text-[#B87333]">Select</span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

const Field = ({ l, v, full }) => (
  <div className={full ? "sm:col-span-2 lg:col-span-3" : ""}>
    <span className="overline text-[#8A8A82]">{l}</span>
    <div className="font-body text-sm text-[#C9C9C2] mt-1">{v}</div>
  </div>
);

function TrackingControl({ api, order, onDone }) {
  const [courier, setCourier] = useState(order.tracking_courier || "");
  const [number, setNumber] = useState(order.tracking_number || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!number) { toast.error("Enter a tracking / LR number."); return; }
    setBusy(true);
    try { await api.patch(`/orders/${order.id}/tracking`, { tracking_courier: courier, tracking_number: number }); toast.success("Tracking saved · buyer emailed"); onDone(); }
    catch { toast.error("Could not save tracking."); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-3 flex flex-wrap items-end gap-3">
      <span className="overline text-[#8A8A82] mr-1">Tracking</span>
      <input data-testid={`courier-${order.id}`} value={courier} onChange={(e) => setCourier(e.target.value)} placeholder="Courier" className="w-36 bg-transparent border-b border-white/20 focus:border-[#B87333] outline-none py-1.5 font-body text-sm text-[#F5F5F0]" />
      <input data-testid={`lr-${order.id}`} value={number} onChange={(e) => setNumber(e.target.value)} placeholder="LR / AWB No." className="w-44 bg-transparent border-b border-white/20 focus:border-[#B87333] outline-none py-1.5 font-body text-sm text-[#F5F5F0]" />
      <button data-testid={`save-tracking-${order.id}`} onClick={save} disabled={busy} className="rounded-full bg-[#F5F5F0] text-black px-5 py-2 font-grotesk text-[10px] uppercase tracking-[0.2em] hover:bg-[#B87333] transition-colors disabled:opacity-50">{busy ? "Saving…" : "Save tracking"}</button>
    </div>
  );
}

function Orders({ api }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { setItems((await api.get("/orders")).data); }
    catch { toast.error("Could not load orders."); }
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const statusColor = { paid: "#2FB999", confirmed: "#2FB999", pending: "#D8B23E", awaiting_gateway: "#D8B23E" };
  const setStatus = async (id, status) => {
    try { await api.patch(`/orders/${id}/status`, { status }); toast.success(`Marked ${status} · buyer emailed`); load(); }
    catch { toast.error("Could not update status."); }
  };
  const STAGES = ["processing", "dispatched", "delivered"];
  if (loading) return <p className="font-body text-[#8A8A82]">Loading…</p>;
  if (items.length === 0) return <p className="font-body text-[#8A8A82]">No orders yet.</p>;
  return (
    <div className="space-y-4" data-testid="admin-order-list">
      {items.map((o) => (
        <div key={o.id} data-testid="admin-order-row" className="border border-white/10 rounded-sm p-6 md:p-8 hover:border-white/20 transition-colors">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-serif text-2xl">{formatINR(o.total)}</span>
                <span className="font-grotesk text-[10px] uppercase tracking-[0.18em] rounded-full px-3 py-1 border" style={{ color: statusColor[o.payment_status] || "#8A8A82", borderColor: `${statusColor[o.payment_status] || "#8A8A82"}55` }}>{o.payment_status}</span>
                <span data-testid={`fulfil-${o.id}`} className="font-grotesk text-[10px] uppercase tracking-[0.18em] rounded-full px-3 py-1 border border-white/20 text-[#C9C9C2]">{o.fulfillment_status || "processing"}</span>
                {o.project_name && <span data-testid={`order-project-${o.id}`} className="font-grotesk text-[10px] uppercase tracking-[0.18em] rounded-full px-3 py-1 border border-[#B87333]/40 text-[#B87333]">Project · {o.project_name}</span>}
              </div>
              <div className="font-body text-sm text-[#8A8A82] mt-1">#{o.id.slice(0, 8).toUpperCase()} · {o.buyer_email} · {o.buyer_category}</div>
            </div>
            <div className="text-right font-body text-xs text-[#8A8A82]">{new Date(o.created_at).toLocaleString()}</div>
          </div>
          <div className="mt-4 pt-4 border-t border-white/10 space-y-1">
            {o.items.map((it) => (
              <div key={it.product_id} className="flex justify-between font-body text-sm text-[#C9C9C2]"><span>{it.quantity} × {it.name}</span><span>{formatINR(it.amount)}</span></div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-white/10 grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-2">
            <Field l="Subtotal" v={formatINR(o.subtotal)} />
            <Field l={`Discount (${Math.round(o.discount_rate * 100)}%)`} v={`− ${formatINR(o.discount)}`} />
            <Field l="GST" v={formatINR(o.gst)} />
            <Field l="Ship to" v={`${o.shipping_name}, ${o.shipping_phone}`} />
            <Field l="GST No." v={o.gst_number || "—"} />
            <Field l="Address" v={o.shipping_address} full />
          </div>
          <div className="mt-5 pt-4 border-t border-white/10 flex flex-wrap items-center gap-2">
            <span className="overline text-[#8A8A82] mr-2">Update status</span>
            {STAGES.map((s) => (
              <button key={s} data-testid={`set-${s}-${o.id}`} onClick={() => setStatus(o.id, s)} disabled={o.fulfillment_status === s}
                className={`rounded-full px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.18em] transition-colors ${o.fulfillment_status === s ? "bg-[#B87333] text-black" : "border border-white/20 text-[#C9C9C2] hover:border-[#B87333]"}`}>
                {s}
              </button>
            ))}
            <button data-testid={`set-cancelled-${o.id}`} onClick={() => setStatus(o.id, "cancelled")} className="rounded-full px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.18em] border border-white/20 text-[#8A8A82] hover:border-[#C0392B] hover:text-[#C0392B] transition-colors">Cancel</button>
          </div>
          <TrackingControl api={api} order={o} onDone={load} />
        </div>
      ))}
    </div>
  );
}

function AddProductForm({ api, onCreated }) {
  const [open, setOpen] = useState(false);
  const [cats, setCats] = useState([]);
  const [saving, setSaving] = useState(false);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ name: "", brand: "", category: "tmt", unit: "per MT", price: "", grade: "", moq: 1, description: "", specs: "" });
  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)).catch(() => {}); }, [api]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.brand || !form.price) { toast.error("Name, brand and price are required."); return; }
    const specsObj = {};
    form.specs.split("\n").forEach((line) => {
      const idx = line.indexOf(":");
      if (idx > 0) specsObj[line.slice(0, idx).trim()] = line.slice(idx + 1).trim();
    });
    const fd = new FormData();
    fd.append("name", form.name); fd.append("brand", form.brand); fd.append("category", form.category);
    fd.append("unit", form.unit); fd.append("price", String(parseInt(form.price, 10) || 0));
    fd.append("grade", form.grade); fd.append("moq", String(parseInt(form.moq, 10) || 1));
    fd.append("description", form.description); fd.append("specs", JSON.stringify(specsObj));
    if (file) fd.append("image", file);
    setSaving(true);
    try {
      await api.post("/products", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Product added.");
      setForm({ name: "", brand: "", category: "tmt", unit: "per MT", price: "", grade: "", moq: 1, description: "", specs: "" });
      setFile(null); setOpen(false); onCreated();
    } catch (err) { toast.error("Could not add product."); }
    finally { setSaving(false); }
  };

  if (!open) return (
    <button data-testid="add-product-toggle" onClick={() => setOpen(true)} className="mb-6 inline-flex items-center gap-2 rounded-full bg-[#F5F5F0] text-black px-6 py-3 font-grotesk text-xs uppercase tracking-[0.2em] hover:bg-[#B87333] transition-colors">+ Add new product</button>
  );

  const inp = "w-full bg-transparent border-b border-white/15 focus:border-[#B87333] outline-none py-2.5 mt-1 font-body text-[#F5F5F0]";
  return (
    <form onSubmit={submit} data-testid="add-product-form" className="mb-8 border border-white/10 rounded-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-serif text-2xl">New product</h3>
        <button type="button" onClick={() => setOpen(false)} className="text-[#8A8A82] hover:text-[#F5F5F0] font-grotesk text-xs uppercase tracking-[0.2em]">Cancel</button>
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <label className="block"><span className="overline text-[#8A8A82]">Name *</span><input data-testid="np-name" name="name" value={form.name} onChange={onChange} className={inp} /></label>
        <label className="block"><span className="overline text-[#8A8A82]">Brand *</span><input data-testid="np-brand" name="brand" value={form.brand} onChange={onChange} className={inp} /></label>
        <label className="block"><span className="overline text-[#8A8A82]">Category</span>
          <select data-testid="np-category" name="category" value={form.category} onChange={onChange} className={`${inp} bg-[#0b0b0b]`}>
            {cats.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        </label>
        <label className="block"><span className="overline text-[#8A8A82]">Unit</span><input data-testid="np-unit" name="unit" value={form.unit} onChange={onChange} placeholder="per MT / per bag (50 kg)" className={inp} /></label>
        <label className="block"><span className="overline text-[#8A8A82]">Price (₹) *</span><input data-testid="np-price" name="price" type="number" value={form.price} onChange={onChange} className={inp} /></label>
        <label className="block"><span className="overline text-[#8A8A82]">Grade</span><input data-testid="np-grade" name="grade" value={form.grade} onChange={onChange} className={inp} /></label>
        <label className="block"><span className="overline text-[#8A8A82]">Min. order qty</span><input data-testid="np-moq" name="moq" type="number" value={form.moq} onChange={onChange} className={inp} /></label>
        <label className="block"><span className="overline text-[#8A8A82]">Product photo</span><input data-testid="np-image" type="file" accept="image/*" onChange={(e) => setFile(e.target.files[0])} className="mt-2 block w-full text-sm text-[#8A8A82] file:mr-4 file:rounded-full file:border-0 file:bg-[#B87333] file:text-black file:px-4 file:py-2 file:font-grotesk file:text-xs file:uppercase file:tracking-wider" /></label>
        <label className="block md:col-span-2"><span className="overline text-[#8A8A82]">Description</span><textarea data-testid="np-desc" name="description" rows={2} value={form.description} onChange={onChange} className={`${inp} resize-none`} /></label>
        <label className="block md:col-span-2"><span className="overline text-[#8A8A82]">Specs (one per line, "Key: Value")</span><textarea data-testid="np-specs" name="specs" rows={3} value={form.specs} onChange={onChange} placeholder={"Grade: Fe 500\nSizes: 8-32 mm"} className={`${inp} resize-none`} /></label>
      </div>
      <button data-testid="np-submit" disabled={saving} className="mt-6 rounded-full bg-[#F5F5F0] text-black px-6 py-3 font-grotesk text-xs uppercase tracking-[0.2em] hover:bg-[#B87333] transition-colors disabled:opacity-50">{saving ? "Saving…" : "Create product"}</button>
    </form>
  );
}

function BulkPrice({ api, cats, onDone }) {
  const [category, setCategory] = useState("tmt");
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const apply = async () => {
    const v = parseFloat(value);
    if (isNaN(v)) { toast.error("Enter a % change (e.g. 5 or -3)."); return; }
    setBusy(true);
    try {
      const r = await api.post("/products/bulk-price", { category, mode: "percent", value: v });
      toast.success(`Updated ${r.data.updated} products by ${v > 0 ? "+" : ""}${v}%`);
      setValue(""); onDone();
    } catch { toast.error("Bulk update failed."); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="bulk-price-panel" className="mb-6 border border-white/10 rounded-sm p-5 flex flex-wrap items-end gap-4">
      <div>
        <div className="overline text-[#8A8A82] mb-1">Bulk price update · category</div>
        <select data-testid="bulk-category" value={category} onChange={(e) => setCategory(e.target.value)} className="bg-[#0b0b0b] border-b border-white/20 focus:border-[#B87333] outline-none py-2 font-body text-[#F5F5F0]">
          {cats.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
      </div>
      <label className="flex flex-col"><span className="overline text-[#8A8A82] mb-1">% change</span>
        <input data-testid="bulk-value" type="number" value={value} onChange={(e) => setValue(e.target.value)} placeholder="+5 / -3" className="w-28 bg-transparent border-b border-white/20 focus:border-[#B87333] outline-none py-2 font-body text-lg text-[#F5F5F0]" />
      </label>
      <button data-testid="bulk-apply" onClick={apply} disabled={busy} className="rounded-full bg-[#B87333] text-black px-6 py-2.5 font-grotesk text-[10px] uppercase tracking-[0.2em] hover:opacity-90 transition-opacity disabled:opacity-50">{busy ? "Applying…" : "Apply to category"}</button>
      <span className="font-body text-xs text-[#8A8A82]">Adjusts every product in the selected category — use for daily rate changes.</span>
    </div>
  );
}

function Products({ api }) {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({});
  const [phaseFilter, setPhaseFilter] = useState("");
  const load = useCallback(async () => {
    setLoading(true);
    try { setItems((await api.get("/products?all=true")).data); }
    catch { toast.error("Could not load products."); }
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); api.get("/categories").then((r) => setCats(r.data)).catch(() => {}); }, [load, api]);

  const saveField = async (id, patch, msg) => {
    try { await api.put(`/products/${id}`, patch); toast.success(msg); load(); }
    catch { toast.error("Update failed."); }
  };
  const savePrice = (p) => {
    const e = edits[p.id] || {};
    const patch = {};
    if (e.price !== undefined) patch.price = parseInt(e.price, 10);
    if (e.moq !== undefined) patch.moq = parseInt(e.moq, 10);
    if (e.stock_qty !== undefined) patch.stock_qty = parseInt(e.stock_qty, 10);
    if (Object.keys(patch).length === 0) return;
    saveField(p.id, patch, "Product updated.");
    setEdits((ed) => { const n = { ...ed }; delete n[p.id]; return n; });
  };

  if (loading) return <p className="font-body text-[#8A8A82]">Loading…</p>;
  const lowStock = items.filter((p) => p.stock_qty !== null && p.stock_qty !== undefined && p.stock_qty <= (p.low_stock_threshold ?? 10));
  const isLow = (p) => p.stock_qty !== null && p.stock_qty !== undefined && p.stock_qty <= (p.low_stock_threshold ?? 10);
  const isPending = (p) => p.price_pending || !p.price;
  const pendingCount = items.filter(isPending).length;
  const visible = items.filter((p) => {
    if (phaseFilter === "pending") return isPending(p);
    if (phaseFilter) return String(p.phase) === phaseFilter;
    return true;
  });
  return (
    <div data-testid="admin-product-list">
      {lowStock.length > 0 && (
        <div data-testid="low-stock-banner" className="mb-6 rounded-sm border border-[#D8B23E]/40 bg-[#D8B23E]/10 px-5 py-4">
          <span className="font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#D8B23E]">⚠ Restock needed</span>
          <div className="font-body text-sm text-[#C9C9C2] mt-1">{lowStock.map((p) => `${p.name} (${p.stock_qty})`).join(" · ")}</div>
        </div>
      )}
      <BulkPrice api={api} cats={cats} onDone={load} />
      <AddProductForm api={api} onCreated={load} />
      <div className="mb-5 flex flex-wrap gap-2" data-testid="admin-phase-filter">
        {[{ v: "", l: `All · ${items.length}` }, { v: "pending", l: `Price pending · ${pendingCount}` }, ...[1, 2, 3, 4, 5, 6, 7, 8].map((n) => ({ v: String(n), l: `Phase ${n} · ${items.filter((p) => p.phase === n).length}` }))].map((f) => (
          <button key={f.v || "all"} data-testid={`admin-phase-${f.v || "all"}`} onClick={() => setPhaseFilter(f.v)}
            className={`rounded-full px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.18em] transition-colors ${phaseFilter === f.v ? "bg-[#F5F5F0] text-black" : "border border-white/15 text-[#C9C9C2] hover:border-[#B87333]"}`}>
            {f.l}
          </button>
        ))}
      </div>
      <div className="space-y-3">
        {visible.map((p) => (
          <div key={p.id} data-testid={`admin-product-${p.id}`} className="border border-white/10 rounded-sm p-5 flex flex-wrap items-center gap-4 hover:border-white/20 transition-colors">
            <img src={resolveImg(p.image)} alt={p.name} className={`w-14 h-14 object-cover rounded-sm border border-white/10 ${p.in_stock === false ? "grayscale opacity-50" : ""}`} />
            <div className="flex-1 min-w-[180px]">
              <div className="font-grotesk text-[10px] uppercase tracking-[0.18em] text-[#B87333]">{p.brand} · {p.phase ? `Phase ${p.phase} · ` : ""}{p.category_label}{p.hsn ? ` · HSN ${p.hsn}` : ""}</div>
              <div className="font-serif text-lg flex items-center gap-2">{p.name}
                {isLow(p) && <span data-testid={`low-${p.id}`} className="font-grotesk text-[9px] uppercase tracking-[0.15em] text-[#D8B23E] border border-[#D8B23E]/50 rounded-full px-2 py-0.5">Low</span>}
                {isPending(p) && <span data-testid={`pending-${p.id}`} className="font-grotesk text-[9px] uppercase tracking-[0.15em] text-[#B87333] border border-[#B87333]/50 rounded-full px-2 py-0.5">Price pending</span>}
              </div>
            </div>
            <label className="flex flex-col"><span className="overline text-[#8A8A82] mb-1">Price ₹ {p.unit ? `(${p.unit})` : ""}{p.price_low > 0 ? ` · range ${p.price_low}–${p.price_high}` : ""}</span>
              <input data-testid={`price-input-${p.id}`} type="number" defaultValue={p.price} onChange={(e) => setEdits((ed) => ({ ...ed, [p.id]: { ...ed[p.id], price: e.target.value } }))} className="w-24 bg-transparent border-b border-white/20 focus:border-[#B87333] outline-none py-1.5 font-body text-lg text-[#F5F5F0]" />
            </label>
            <label className="flex flex-col"><span className="overline text-[#8A8A82] mb-1">MOQ</span>
              <input data-testid={`moq-input-${p.id}`} type="number" defaultValue={p.moq} onChange={(e) => setEdits((ed) => ({ ...ed, [p.id]: { ...ed[p.id], moq: e.target.value } }))} className="w-16 bg-transparent border-b border-white/20 focus:border-[#B87333] outline-none py-1.5 font-body text-lg text-[#F5F5F0]" />
            </label>
            <label className="flex flex-col"><span className="overline text-[#8A8A82] mb-1">Stock qty</span>
              <input data-testid={`stock-input-${p.id}`} type="number" defaultValue={p.stock_qty ?? ""} placeholder="∞" onChange={(e) => setEdits((ed) => ({ ...ed, [p.id]: { ...ed[p.id], stock_qty: e.target.value } }))} className="w-20 bg-transparent border-b border-white/20 focus:border-[#B87333] outline-none py-1.5 font-body text-lg text-[#F5F5F0]" />
            </label>
            <button data-testid={`stock-toggle-${p.id}`} onClick={() => saveField(p.id, { in_stock: !(p.in_stock !== false) }, "Stock updated.")} className={`rounded-full px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.18em] border transition-colors ${p.in_stock === false ? "border-[#C0392B]/50 text-[#C0392B]" : "border-[#2FB999]/50 text-[#2FB999]"}`}>
              {p.in_stock === false ? "Out of stock" : "In stock"}
            </button>
            <button data-testid={`save-price-${p.id}`} onClick={() => savePrice(p)} disabled={edits[p.id] === undefined} className="rounded-full bg-[#F5F5F0] text-black px-5 py-2 font-grotesk text-[10px] uppercase tracking-[0.2em] hover:bg-[#B87333] transition-colors disabled:opacity-30">Save</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function SRNIntakePulseTile({ onOpen }) {
  const { api } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/admin/srn-analytics/today").then((r) => setD(r.data)).catch(() => {});
  }, [api]);

  const KIND_LABEL = {
    rfq: "Quote", quote: "Quote", kyc: "KYC", refund: "Refund",
    indirect_commission: "Indirect", concierge: "Concierge",
    order: "Order", motion: "Motion", inquiry: "Inquiry",
  };
  const KIND_COLOR = {
    rfq: "#C9A961", quote: "#C9A961", kyc: "#2FB999",
    refund: "#C0392B", indirect_commission: "#C0392B",
    concierge: "#B87333", order: "#C9A961",
    motion: "#A6821E", inquiry: "#8A8A82",
  };

  const total = d?.total ?? 0;
  const active = total > 0;
  const kinds = Object.entries(d?.by_kind || {}).slice(0, 6);
  const mono = 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace';

  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid="srn-intake-tile"
      className={`w-full text-left rounded-sm border p-6 md:p-7 mb-4 transition-colors flex items-center justify-between gap-6 ${active ? "border-[#C9A961]/60 bg-[#C9A961]/[0.06] hover:border-[#C9A961]" : "border-white/10 bg-black/40 hover:border-[#C9A961]/40"}`}
    >
      <div className="min-w-0">
        <div className="font-grotesk text-[10px] uppercase tracking-[0.32em]" style={{ color: active ? "#C9A961" : "#8A8A82" }}>
          Trade Desk Intake · SRNs opened today
        </div>
        <div className="font-serif text-2xl md:text-3xl text-[#F5F5F0] mt-2">
          {active ? `${total} case file${total === 1 ? "" : "s"} filed today` : "No case files filed yet today"}
        </div>
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 font-body text-[12px] text-[#C9C9C2]">
          {kinds.length === 0
            ? <span className="text-[#6f6f68]">Awaiting first intake…</span>
            : kinds.map(([k, n]) => (
                <span key={k} className="inline-flex items-center gap-1.5">
                  <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: KIND_COLOR[k] || "#8A8A82" }} />
                  <span className="text-[#F5F5F0] font-medium tabular-nums">{n}</span>
                  <span className="text-[#8A8A82]">{KIND_LABEL[k] || k}</span>
                </span>
              ))}
        </div>
      </div>
      <div className="flex items-center gap-4 shrink-0">
        <div className="text-right">
          <div className="font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#8A8A82]">Today</div>
          <div className="font-serif text-5xl tabular-nums leading-none mt-1" style={{ color: active ? "#C9A961" : "#8A8A82", fontFamily: mono }} data-testid="srn-intake-count">
            {String(total).padStart(2, "0")}
          </div>
        </div>
        <span className={`hidden md:inline-flex items-center rounded-full border px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.28em] ${active ? "border-[#C9A961]/60 text-[#C9A961]" : "border-white/20 text-[#8A8A82]"}`}>
          Open ledger →
        </span>
      </div>
    </button>
  );
}

function BoardEscalationTile({ count, onOpen }) {
  const active = count > 0;
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid="board-escalation-tile"
      className={`w-full text-left rounded-sm border p-6 md:p-7 mb-10 transition-colors flex items-center justify-between gap-6 ${active ? "border-[#C0392B]/60 bg-[#C0392B]/[0.08] hover:border-[#C0392B]" : "border-white/10 bg-black/40 hover:border-[#C9A961]/40"}`}
    >
      <div>
        <div className="font-grotesk text-[10px] uppercase tracking-[0.32em]" style={{ color: active ? "#C0392B" : "#8A8A82" }}>
          Level-1 Board · Escalation Queue
        </div>
        <div className="font-serif text-2xl md:text-3xl text-[#F5F5F0] mt-2">
          {active ? `${count} indirect claim${count === 1 ? "" : "s"} awaiting Board sanction` : "No claims awaiting Board sanction"}
        </div>
        <div className="font-body text-[12px] text-[#8A8A82] mt-2 max-w-2xl">
          0%-slab entries (fulfillment-to-applying gap &gt; 45 days). One click to review and issue sanction.
        </div>
      </div>
      <div className="flex items-center gap-4">
        <div className="text-right">
          <div className="font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#8A8A82]">Pending</div>
          <div className="font-serif text-5xl tabular-nums leading-none mt-1" style={{ color: active ? "#C0392B" : "#8A8A82" }} data-testid="board-escalation-count">
            {count}
          </div>
        </div>
        <span className={`hidden md:inline-flex items-center rounded-full border px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.28em] ${active ? "border-[#C0392B]/60 text-[#C0392B]" : "border-white/20 text-[#8A8A82]"}`}>
          Open queue →
        </span>
      </div>
    </button>
  );
}

function Analytics({ api, boardEscalationCount, onOpenBoardQueue, onOpenSRNLedger }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/analytics").then((r) => setD(r.data)).catch(() => toast.error("Could not load analytics.")); }, [api]);
  if (!d) return <p className="font-body text-[#8A8A82]">Loading…</p>;
  const CAT = { super_stockist: "Super-Stockist", wholesaler: "Wholesaler", dealer: "Dealer", booking_agent: "Booking Agent", other: "Other" };
  const cards = [
    { l: "Revenue (paid)", v: formatINR(d.revenue) },
    { l: "Paid orders", v: d.paid_orders },
    { l: "Total orders", v: d.total_orders },
    { l: "Avg order value", v: formatINR(d.avg_order) },
  ];
  const maxCat = Math.max(1, ...Object.values(d.by_category));
  return (
    <div data-testid="admin-analytics">
      <SRNIntakePulseTile onOpen={onOpenSRNLedger} />
      <AdminPulseAnalyticsTile />
      <AdminIMAPHealthTile />
      <AdminPayoutsTile />
      <AdminLivePulseTile />
      <BoardEscalationTile count={boardEscalationCount || 0} onOpen={onOpenBoardQueue} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-10">
        {cards.map((c) => (
          <div key={c.l} className="bg-[#070707] p-6 md:p-8">
            <div className="overline text-[#8A8A82]">{c.l}</div>
            <div className="font-serif text-3xl md:text-4xl mt-2">{c.v}</div>
          </div>
        ))}
      </div>
      <div className="grid md:grid-cols-2 gap-8">
        <div className="border border-white/10 rounded-sm p-6">
          <h3 className="font-serif text-2xl mb-5">Revenue by tier</h3>
          {Object.keys(d.by_category).length === 0 ? <p className="font-body text-sm text-[#8A8A82]">No paid orders yet.</p> : Object.entries(d.by_category).map(([k, v]) => (
            <div key={k} className="mb-4">
              <div className="flex justify-between font-body text-sm mb-1"><span className="text-[#C9C9C2]">{CAT[k] || k}</span><span>{formatINR(v)}</span></div>
              <div className="h-2 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-[#B87333]" style={{ width: `${(v / maxCat) * 100}%` }} /></div>
            </div>
          ))}
        </div>
        <div className="border border-white/10 rounded-sm p-6">
          <h3 className="font-serif text-2xl mb-5">Top products</h3>
          {d.top_products.length === 0 ? <p className="font-body text-sm text-[#8A8A82]">No sales yet.</p> : d.top_products.map((p, i) => (
            <div key={p.name} className="flex items-center justify-between py-2.5 border-b border-white/10">
              <span className="font-body text-sm text-[#C9C9C2]"><span className="text-[#B87333] mr-2">{i + 1}</span>{p.name}</span>
              <span className="font-serif">{formatINR(p.revenue)}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-10">
        <AdminHierarchyTree />
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, logout, api } = useAuth();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Two-way bind the active tab with the URL query param so deep-links
  // (e.g. /admin?tab=price-lock) land straight on the requested panel and
  // Back/Forward remember the last section.
  const [tab, _setTab] = useState(() => searchParams.get("tab") || "overview");
  const setTab = useCallback((next) => {
    _setTab(next);
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (next && next !== "overview") p.set("tab", next); else p.delete("tab");
      return p;
    }, { replace: true });
  }, [setSearchParams]);
  useEffect(() => {
    const q = searchParams.get("tab") || "overview";
    if (q !== tab) _setTab(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const [kycPending, setKycPending] = useState(0);
  const [approvalsPending, setApprovalsPending] = useState(0);
  const [unreadNotifs, setUnreadNotifs] = useState(0);
  const [directorPending, setDirectorPending] = useState(0);
  const [boardEscalationCount, setBoardEscalationCount] = useState(0);
  const [indirectInitialFilter, setIndirectInitialFilter] = useState("");
  const { open: paletteOpen, setOpen: setPaletteOpen } = useSRNPalette();
  const [paletteJumpTo, setPaletteJumpTo] = useState(null);

  // When the palette selects a row, route to the SRN Ledger tab and let
  // that panel focus the case file via the srn hint.
  const handlePaletteNavigate = useCallback((row) => {
    setTab("srn-ledger");
    setPaletteJumpTo(row.srn);
  }, []);

  const loadBoardEscalationCount = useCallback(() => {
    api.get("/admin/indirect-claims/escalation-count").then((r) => setBoardEscalationCount(r.data.count || 0)).catch(() => {});
  }, [api]);

  const openBoardEscalationQueue = () => {
    setIndirectInitialFilter("board_escalation");
    setTab("indirect-claims");
  };

  const loadDirectorCount = useCallback(() => {
    api.get("/admin/directors/pending-count").then((r) => setDirectorPending(r.data.count || 0)).catch(() => {});
  }, [api]);

  const loadKycCount = useCallback(() => {
    api.get("/kyc/pending-count").then((r) => setKycPending(r.data.count)).catch(() => {});
  }, [api]);
  const loadApprovalsCount = useCallback(() => {
    api.get("/admin/users/pending-count").then((r) => setApprovalsPending(r.data.count)).catch(() => {});
  }, [api]);
  const loadUnreadNotifs = useCallback(() => {
    api.get("/admin/notifications?unread_only=true&limit=1").then((r) => setUnreadNotifs(r.data.unread || 0)).catch(() => {});
  }, [api]);
  useEffect(() => { loadKycCount(); loadApprovalsCount(); loadUnreadNotifs(); loadDirectorCount(); loadBoardEscalationCount(); }, [loadKycCount, loadApprovalsCount, loadUnreadNotifs, loadDirectorCount, loadBoardEscalationCount]);

  const doLogout = async () => { await logout(); nav("/"); };
  const badges = { kyc: kycPending, approvals: approvalsPending, notifs: unreadNotifs, director: directorPending };

  const render = { overview: <><KpiDashboard api={api} email={user?.email} /><QuickActions onSelect={setTab} badges={badges} /><Analytics api={api} boardEscalationCount={boardEscalationCount} onOpenBoardQueue={openBoardEscalationQueue} onOpenSRNLedger={() => setTab("srn-ledger")} /></>, "director-suite": <div className="space-y-6"><AdminChairmanSignature api={api} /><AdminDirectorInvites api={api} /><KYCReviewPanel api={api} /><AdminDirectorSuite api={api} /></div>, "founder-concierge": <AdminFounderConcierge api={api} />, "pioneers": <PioneersPanel api={api} />, "payment-rails": <AdminPaymentRails api={api} />, "pioneer-access-control": <PioneerAccessControl api={api} />, "board-motions": <AdminBoardMotions api={api} />, "refund-compliance": <AdminRefundCompliance />, "compliance-log": <AdminComplianceLog />, "fee-management": <AdminFeeManagement />, "indirect-claims": <AdminIndirectClaims initialFilter={indirectInitialFilter} />, "srn-ledger": <AdminSRNLedger />, "legacy-goodwill": <AdminLegacyGoodwill api={api} />, "data-governance": <AdminDataGovernance api={api} />, notifications: <AdminNotifications api={api} onUnreadChange={setUnreadNotifs} />, communications: <AdminCommunications api={api} />, "brand-assets": <AdminBrandAssets api={api} />, "commission-desk": <AdminCommissionDesk api={api} />, "deal-protection": <AdminDealProtection api={api} />, "seller-ranking": <AdminSellerRanking api={api} />, "big-cap": <AdminBigCapLeague api={api} />, certificates: <AdminCertificates api={api} />, meetings: <div className="pt-2"><GoogleMeetConnectionCard api={api} /><MeetingsPanel api={api} embedded /></div>, "invoice-hub": <AdminInvoiceHub api={api} />, "letterhead-studio": <AdminLetterheadStudio api={api} />, "reinstatements": <AdminReinstatementInbox api={api} />, "virtual-meetings": <AdminVirtualMeetings api={api} />, ledger: <AdminMasterLedger api={api} />, operations: <OperationsMap api={api} />, inquiries: <Inquiries api={api} />, "quote-requests": <QuoteRequests api={api} />, "rfq-broadcast": <AdminRfqBroadcast api={api} />, "buyer-quotations": <AdminBuyerQuotations api={api} />, "price-lock": <AdminPriceLockBoard api={api} />, "wallet": <AdminWalletPanel api={api} />, "payouts": <AdminPayoutsDesk api={api} />, "treasury-desk": <AdminTreasuryDesk api={api} />, "banking-onboarding": <AdminTreasuryOnboarding api={api} />, "vcb": <AdminVCB api={api} />, "property-desk": <AdminPropertyDesk api={api} />, "vinci-manual": <AdminManual api={api} />, "zoho-books": <AdminZohoBooksPanel api={api} />, "careers": <AdminCareersPanel api={api} />, "legal-desk": <AdminLegalDeskPanel api={api} />, "live-pulse": <AdminLivePulsePanel />, "monumental-studio": <AdminMonumentalStudio api={api} />, "compliance-capsule": <AdminComplianceCapsule api={api} />, "banker-report": <AdminBankerReport api={api} />, "sovereign-ledger": <AdminSovereignLedger api={api} />, "signatory-vault": <AdminSignatoryVault api={api} />, "seal-desk": <AdminSealDesk api={api} />, "gst-desk": <AdminGstDesk api={api} />, billdesk: <AdminBillDesk api={api} />, users: <div className="space-y-6"><AdminBatchLinkDependents api={api} /><Users api={api} /></div>, "onboarding-inbox": <AdminOnboardingInbox api={api} />, "credit-limits": <AdminCreditLimits api={api} />, "settlement-desk": <AdminSettlementDesk api={api} />, approvals: <UserApprovals api={api} onCount={loadApprovalsCount} />, verifications: <KycVerifications api={api} onCount={loadKycCount} />, agreements: <AgreementsManager api={api} />, orders: <Orders api={api} />, products: <Products api={api} />, rates: <div className="space-y-6"><AdminPricePulseCard /><DailyRates api={api} /></div>, "financial-rules": <AdminFinancialRules />, rewards: <RewardsLeaderboard api={api} />, claims: <Claims api={api} />, quotations: <Quotations api={api} />, requirements: <RFQs api={api} />, entities: <BillingEntities api={api} /> };

  return (
    <AdminShell active={tab} onSelect={setTab} badges={badges} user={user} onLogout={doLogout} onOpenPalette={() => setPaletteOpen(true)}>
      <Toaster position="bottom-right" theme="dark" richColors />
      <AdminSRNPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        onNavigate={handlePaletteNavigate}
      />
      <div className="max-w-[1500px]">
        {render[tab] || <div className="admin-card text-sm text-[#888888]">Unknown section.</div>}
      </div>
    </AdminShell>
  );
}

